import {ValidateRules} from "@/core/types";
import {computed, Ref, ref, toRefs} from "vue";
import {DefinedProps} from "@/core/vue/types";
import {useVorderValidate} from "@/modules/shop/store/vorder-validate";
import {storeToRefs} from "pinia";
import {BenefitTypeEnum, VorderError, VOrderSectionEnum} from "@/gql/gen";
import BenefitDialog from "@/components/Shop/VOrder/ui/Dialog/Benefit.vue";
import {useStackRouter} from "@/packages/stack-router/hooks/useStackRouter";
import {useVorderState} from "@/modules/shop/store/vorder-state";
import {useBasketRulesStore} from "@/modules/shop/store/basket-rules";
import {Dialog} from 'quasar';
import {useVorderCoupon} from "@/modules/shop/store/vorder-coupon";

export type VOrderSectionWrapperProps = {
    hideErrors?: boolean
    code: VOrderSectionEnum
    label?: string
    hideLabel?: boolean
    hint?: string
    rules?: ValidateRules
    loading?: boolean
    loadingText?: string
    icon?: any
    onClick?: any
    colClass?: any
    colStyle?: any
    iconSize?: any
    active?: boolean
    filled?: boolean
    mode?: 'compact' | 'full'
}

export type VOrderSectionProps = VOrderSectionWrapperProps & {}

export const vorderSectionPropsDefault = {
    loading: false,
}

export const vorderSectionWrapperPropsDefault = {
    loading: false,
}

export type VOrderSectionEmits = {
    (e: 'click', code: string): void
    (e: 'open', code: string): void
}

const benefitTypesData: Record<BenefitTypeEnum, {
    labelTo: string,
    labelTarget: string,
}> = {
    prize: {
        labelTarget: 'приз',
        labelTo: 'приза',
    },
    discount: {
        labelTarget: 'скидку',
        labelTo: 'скидки',
    },
    gift: {
        labelTarget: 'подарок',
        labelTo: 'подарка',
    },
    bonus: {
        labelTarget: 'бонусы',
        labelTo: 'бонусов',
    },
    coupon: {
        labelTarget: 'промокод',
        labelTo: 'промокода',
    },
}

export function useVorderSectionWrapper(props: DefinedProps<VOrderSectionWrapperProps>, model: Ref<any>, emit: VOrderSectionEmits) {

    const vorderState = useVorderState()
    const validateStore = useVorderValidate()
    const basketRulesStore = useBasketRulesStore()
    const vorderCoupon = useVorderCoupon()

    const {benefitType} = storeToRefs(vorderState)
    const {validateResultBySection} = storeToRefs(validateStore)
    const {canSetBenefitType, setBenefitType} = basketRulesStore

    const {rules, iconSize, code, filled} = toRefs(props)

    const validated = ref<boolean | null>()

    const stackRouter = useStackRouter()

    const click = () => {
        emit('click', code.value)
        emit('open', code.value)

        props.onClick && props.onClick(code.value)

        switch (props.code) {
            case "benefit_bonus":
                onClickBenefit('bonus')
                break;
            case "benefit_coupon":
                onClickBenefit('coupon')
                break;
            case "benefit_discount":
                onClickBenefit('discount')
                break;
            case "benefit_prize":
                onClickBenefit('prize')
                break;
        }
    }

    const onClickBenefit = (type: BenefitTypeEnum, confirmed = false) => {
        const benefitTypeData = benefitTypesData[type]
        if (benefitType.value !== type && canSetBenefitType(type)) {

            if (!confirmed) {
                if (type !== 'coupon' && vorderCoupon.hasCoupon) {
                    Dialog.create({
                        title: 'Внимание: будет удален промокод',
                        message: 'У вас есть применный к заказу помокод. Использование ' + benefitTypeData.labelTo + ' отменяет действие промокода',
                        cancel: 'оставить промокод',
                        ok: 'использовать ' + benefitTypeData.labelTarget,
                    }).onOk(() => {
                        onClickBenefit(type, true)
                    }).onCancel(() => {
                        console.log('>>>> Cancel')
                    })
                    return;
                }
            }

            setBenefitType(type)

        } else {
            stackRouter.getStack()?.push(BenefitDialog, {role: 'vorder', openBenefitType: type})
        }
    }

    const error = computed<VorderError>(() => {
        return validateResultBySection.value[code.value] as any
    })

    const errorMessage = computed(() => {
        return error.value?.message
    })

    const haveErrors = computed(() => {
        if (props.hideErrors) {
            return false
        }
        return !!error.value
    })

    const hover = ref(false)

    const isFilled = computed(() => {
        return filled.value || !!model.value
    })

    const validate = async () => {
        validateStore.validate(code.value)
        validated.value = true
    }

    const resetValidation = () => {
        validateStore.resetValidation(code.value)
        validated.value = false
    }

    return {
        click,
        validate,
        resetValidation,
        validated,
        haveErrors,
        errorMessage,
        error,
        hover,
        isFilled
    }
}
